import {
  ADMIN_CHAT,
  AdminHeader,
  AdminOptionsDropdown,
  AdminSpinner,
  CHAT_CONVERSATION_PARAM,
  ChatIcon,
  ClientPaymentsIconDD,
  Disclaimer,
  Div,
  DropdownDeleteIcon,
  DropdownNotesIcon,
  getLocationsByUserId,
  getUserById,
  H3,
  H5,
  lightTheme,
  LocationPinIcon,
  openDirectConversation,
  openSupportConversation,
  P,
  PhoneNumberText,
  Pill,
  RecoverIcon,
  renderRole,
  Separator,
  SimpleLocationEntity,
  useAuthenticatedAuthUser,
  User,
  usePermissions,
  UserPermission,
  UserRole,
  isInternalRole,
  reportError,
} from '@vgrubs/components';
import { useInvalidateQueries } from 'components/src/hooks/useInvalidateQueries';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Link } from 'react-router-dom';
import { KeyValueInfo } from '../../Verifications/components/KeyValueInfo';
import { SectionCard } from '../../Verifications/components/SectionCard';
import { UsersAndAccessDeleteModal } from '../UsersAndAccessDelete';
import { UsersAndAccessEditModal } from '../UsersAndAccessEdit';
import { UsersAndAccessRecoverModal } from '../UsersAndAccessRecover';
import { BankWallet } from './BankWallet';
import { UserPermissions } from './UserPermissions';

export const UserDetailsPage = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingBank, setLoadingBank] = useState(false);
  const [locations, setLocations] = useState<SimpleLocationEntity[]>([]);
  const { id } = useParams() as { id: string };
  const [deleteModal, setDeleteModal] = useState<User | null>(null);
  const [editModal, setEditModal] = useState<User | null>(null);
  const [recoverModal, setRecoverModal] = useState<User | null>(null);
  const navigate = useNavigate();
  const { role, id: viewerId } = useAuthenticatedAuthUser();
  const { can } = usePermissions();

  const hasLocations =
    user?.role !== UserRole.admin &&
    user?.role !== UserRole.support &&
    user?.role !== UserRole.marketing;

  // Only an admin can grant, and only support/marketing need granting -
  // admins already hold every permission implicitly.
  const hasChatAccess =
    role === UserRole.admin &&
    (user?.role === UserRole.support || user?.role === UserRole.marketing);

  // The card beside Profile: a merchant's locations, or an internal user's
  // chat access. The two never apply to the same person.
  const sideCard = hasLocations || hasChatAccess;

  const hasBank = user?.role === UserRole.partner && role === UserRole.admin;

  /*
   * Which half of the inbox this person belongs to. A colleague is a direct
   * message; everybody else - merchants, brand owners, their staff - is a
   * support thread, which is the one kind that needs the write permission to
   * start.
   *
   * Sales is not a colleague here: they briefly were, while the backend was
   * moving their app onto the internal chat, and that was reverted - an agent is
   * answered in the support queue's own Sales tab like anybody else outside.
   * `INTERNAL_ROLES` is the one place that decision lives.
   */
  const isColleague = isInternalRole(user?.role);

  const canOpenConversation =
    !!user &&
    user.id !== viewerId &&
    !user.deleted &&
    can(UserPermission.conversationRead) &&
    (isColleague || can(UserPermission.conversationWrite));

  /**
   * Both endpoints are idempotent - called twice for the same person they hand
   * back the same conversation - so there is nothing to check first and no way
   * to end up with two threads for one user.
   *
   * The scope goes into the link as well as the id: the inbox only lists one
   * half at a time, and landing on a thread the list beside it does not contain
   * looks like the page failed to select anything.
   */
  const openConversation = async () => {
    if (!user) return;
    try {
      const { data } = isColleague
        ? await openDirectConversation(user.id)
        : await openSupportConversation(user.id);
      navigate(
        `${ADMIN_CHAT}?${isColleague ? 'scope=internal&' : ''}` +
          `${CHAT_CONVERSATION_PARAM}=${data.data.id}`
      );
    } catch (error) {
      reportError(error);
    }
  };

  async function getUserLocations() {
    try {
      setLoading(true);
      const { data } = await getLocationsByUserId(id);
      setLocations(data.data);
      setLoading(false);
    } catch (error) {
      reportError(error);
      setLoading(false);
    }
  }
  const { invalidateByPrefix } = useInvalidateQueries();

  async function getUserData(silent = false) {
    try {
      if (!silent) {
        setLoading(true);
      }
      const { data } = await getUserById(id);
      setUser(data.data);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    getUserData();
    if (hasLocations) getUserLocations();
  }, []);

  if (loading) return <AdminSpinner />;

  if (!user) {
    return null;
  }

  const actions = [
    {
      label: 'Edit User',
      onClick: () => setEditModal(user),
      Icon: DropdownNotesIcon,
    },

    ...(canOpenConversation
      ? [
          {
            label: 'Open Conversation',
            onClick: openConversation,
            Icon: ChatIcon,
          },
        ]
      : []),

    ...(user.role === UserRole.partner
      ? [
          {
            label: 'Transactions',
            onClick: () =>
              navigate(`/transactions/${user?.id}`, { state: user }),
            Icon: ClientPaymentsIconDD,
          },
        ]
      : []),
    {
      label: user.deleted ? 'Recover User' : 'Delete User',
      onClick: () => {
        if (user.deleted) {
          setRecoverModal(user);
        } else {
          setDeleteModal(user);
        }
      },
      Icon: user.deleted ? RecoverIcon : DropdownDeleteIcon,
      color: lightTheme.primary,
    },
  ];

  return (
    <>
      {loadingBank && <AdminSpinner />}
      <AdminHeader title={'User Details'}>
        <AdminOptionsDropdown actions={actions} />
      </AdminHeader>
      <Div $flex $column $gap="8px" $m="32px 0" $mSM="20px 0" $width="100%">
        <H3 $mb={0}>
          <span>{user?.firstName + ' ' + user?.lastName}</span>
        </H3>
        {user?.deleted && (
          <div>
            <Pill $background={lightTheme.darkGray}>
              <Disclaimer $m="0" $color={lightTheme.white}>
                Inactive
              </Disclaimer>
            </Pill>
          </div>
        )}
      </Div>

      <Div $flex $alignStart $columnSM $gap="32px" $gapSM="20px" $mb="32">
        <Div $width={sideCard ? '40%' : '50%'} $widthSM="100%">
          <SectionCard title="Profile">
            {user.email && (
              <a href={`mailto:${user.email}`}>
                <KeyValueInfo k="Email" v={user.email} />
              </a>
            )}
            {user.phoneNumber && (
              <KeyValueInfo
                k="Phone"
                v={user.phoneNumber}
                component={<PhoneNumberText phone={user.phoneNumber} title />}
              />
            )}
            <KeyValueInfo k="Role" v={renderRole(user.role)} last />
          </SectionCard>
        </Div>

        {sideCard && (
          <Div $width="60%" $widthSM="100%">
            {hasLocations ? (
              <SectionCard
                title="Locations"
                action={
                  locations.length > 0 && (
                    <Disclaimer $m="0" $color={lightTheme.darkGray}>
                      {locations.length}{' '}
                      {locations.length === 1 ? 'location' : 'locations'}
                    </Disclaimer>
                  )
                }
              >
                {locations.map(({ name, id }, i) => (
                  <Link to={`/locations/${id}`} key={id}>
                    {i > 0 && <Separator />}
                    <Div $flex $between $alignCenter $gap="16px" $p="14px 0">
                      <H5 $m="0">{name}</H5>
                      <LocationPinIcon />
                    </Div>
                  </Link>
                ))}
                {locations.length < 1 && (
                  <P $m="0" $color={lightTheme.darkGray}>
                    No locations yet.
                  </P>
                )}
              </SectionCard>
            ) : (
              <UserPermissions userId={id} />
            )}
          </Div>
        )}
      </Div>

      {locations.length > 0 && hasBank && (
        <BankWallet userId={id} setLoading={setLoadingBank} user={user} />
      )}

      <UsersAndAccessDeleteModal
        setLoading={setLoading}
        closeModal={() => setDeleteModal(null)}
        modalIsOpen={deleteModal}
        onSuccess={() => {
          setDeleteModal(null);
          getUserData(true);
          invalidateByPrefix('/v1/admin/users');
        }}
      />

      <UsersAndAccessEditModal
        modalIsOpen={editModal}
        closeModal={() => setEditModal(null)}
        setLoading={setLoading}
        setUserDetailsModal={(u) => {
          setUser(u);
        }}
        onSuccess={() => {
          invalidateByPrefix('/v1/admin/users');
        }}
      />

      <UsersAndAccessRecoverModal
        setLoading={setLoading}
        closeModal={() => setRecoverModal(null)}
        modalIsOpen={recoverModal}
        onSuccess={() => {
          setRecoverModal(null);
          getUserData(true);
          invalidateByPrefix('/v1/admin/users');
        }}
      />
    </>
  );
};
